import { useEffect, useRef, useState } from "react";

interface MultiSelectProps {
  label: string;
  options: (string | number)[];
  selected: (string | number)[];
  onToggle: (value: string | number) => void;
  format?: (value: string | number) => string;
}

function MultiSelect({ label, options, selected, onToggle, format }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex h-10 items-center gap-2 rounded-md border px-3 text-sm transition-colors cursor-pointer whitespace-nowrap ${
          selected.length
            ? "border-primary-400 bg-primary-50 text-primary-700"
            : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
        }`}
      >
        {label}
        {selected.length > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-[11px] font-semibold text-background-50">
            {selected.length}
          </span>
        )}
        <span className="flex h-4 w-4 items-center justify-center">
          <i className={`ri-arrow-down-s-line transition-transform ${open ? "rotate-180" : ""}`}></i>
        </span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-2 max-h-64 w-52 overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-2 animate-scale-in">
          {options.map((opt) => {
            const active = selected.includes(opt);
            return (
              <button
                key={String(opt)}
                type="button"
                onClick={() => onToggle(opt)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded border ${
                    active ? "border-primary-500 bg-primary-500 text-background-50" : "border-background-400"
                  }`}
                >
                  {active && <i className="ri-check-line text-[11px]"></i>}
                </span>
                {format ? format(opt) : opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export interface PendingFilters {
  genres: string[];
  regions: string[];
  years: number[];
}

interface FilterBarProps {
  genreOptions: string[];
  regionOptions: string[];
  yearOptions: number[];
  pending: PendingFilters;
  activeCount: number;
  resultCount: number;
  onToggle: (kind: keyof PendingFilters, value: string | number) => void;
  onApply: () => void;
  onReset: () => void;
}

export default function FilterBar({
  genreOptions,
  regionOptions,
  yearOptions,
  pending,
  activeCount,
  resultCount,
  onToggle,
  onApply,
  onReset,
}: FilterBarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const groups = (
    <>
      <MultiSelect
        label="类型"
        options={genreOptions}
        selected={pending.genres}
        onToggle={(v) => onToggle("genres", v)}
      />
      <MultiSelect
        label="地区"
        options={regionOptions}
        selected={pending.regions}
        onToggle={(v) => onToggle("regions", v)}
      />
      <MultiSelect
        label="年份"
        options={yearOptions}
        selected={pending.years}
        onToggle={(v) => onToggle("years", v)}
        format={(v) => `${v} 年`}
      />
    </>
  );

  const actions = (
    <>
      <button
        type="button"
        onClick={onApply}
        className="flex h-10 items-center gap-2 rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-filter-3-line"></i>
        </span>
        应用筛选
      </button>
      <button
        type="button"
        onClick={onReset}
        className="flex h-10 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer whitespace-nowrap"
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-refresh-line"></i>
        </span>
        重置
      </button>
    </>
  );

  return (
    <>
      <div className="hidden flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 lg:flex">
        <span className="flex items-center gap-1.5 text-sm font-medium text-foreground-800">
          <span className="flex h-4 w-4 items-center justify-center text-primary-600">
            <i className="ri-equalizer-line"></i>
          </span>
          筛选
        </span>
        {groups}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-foreground-500">
            当前 {resultCount} 部{activeCount > 0 ? ` · 已选 ${activeCount} 项` : ""}
          </span>
          {actions}
        </div>
      </div>

      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-background-200 bg-background-50 text-sm font-medium text-foreground-800 cursor-pointer"
        >
          <span className="flex h-4 w-4 items-center justify-center text-primary-600">
            <i className="ri-filter-3-line"></i>
          </span>
          筛选与排序
          {activeCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-[11px] font-semibold text-background-50">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-primary-950/50"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-4 animate-fade-up">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-950">筛选条件</span>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="flex flex-col gap-3">{groups}</div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  onReset();
                }}
                className="flex h-11 items-center justify-center rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => {
                  onApply();
                  setDrawerOpen(false);
                }}
                className="flex h-11 items-center justify-center rounded-md bg-primary-500 text-sm font-semibold text-background-50 cursor-pointer"
              >
                应用筛选
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}